/* eslint-disable unicorn/filename-case */
import { useCallback, useEffect, useState } from "react";
import useWebSocket from "react-use-websocket";
import { Output } from "valibot";

import { generationModes } from "shared/constants";
import { noop } from "shared/functions";
import { useGlobalsContext } from "shared/hooks";
import { getIpLookup } from "shared/ipLookup";
import { AppConfiguration, GenerationMode } from "shared/types";
import { removePeriodsAfterURL } from "shared/utils/sourceUtils";

import { ChatDataSchema, LiveAgentHandoffInfo } from "./schemas";

function useConnectWebsocket(
  botId: string,
  config: AppConfiguration,
  supportEmail: string | null,
  chatId: string,
  setGenerationMode: React.Dispatch<React.SetStateAction<GenerationMode>>,
  setChatData: React.Dispatch<
    React.SetStateAction<Output<typeof ChatDataSchema>>
  >,
  setMessage: React.Dispatch<React.SetStateAction<string>>,
  useWebsocketGeneration: boolean,
  setUseWebsocketGeneration: React.Dispatch<React.SetStateAction<boolean>>,
  setLiveAgentHandoffInfo: React.Dispatch<
    React.SetStateAction<Output<typeof LiveAgentHandoffInfo> | null>
  >,
) {
  const { getParentUrl } = useGlobalsContext();
  const [ipAddress, setIpAddress] = useState<string>();
  const [socketUrl, setSocketUrl] = useState<string | null>(null);

  const { sendMessage, lastMessage } = useWebSocket(
    socketUrl,
    {
      heartbeat: {
        message: "ping",
        returnMessage: '{"status":"success"}',
        timeout: 120_000, // 2 minutes, if no response is received, the connection will be closed
        interval: 60_000, // every 1 minute, a ping message will be sent
      },
      shouldReconnect: closeEvent => {
        return true;
      },
      onError: event => {
        // eslint-disable-next-line no-console
        console.error("WebSocket error", event);
      },
      reconnectAttempts: 10,
      reconnectInterval: 1_000,
    },
    useWebsocketGeneration,
  );

  useEffect(() => {
    const websocketToken = process.env.REACT_APP_WEBSOCKET_TOKEN;
    if (!websocketToken || !config.token || !chatId) {
      return;
    }
    setSocketUrl(`${websocketToken}?token=${config.token}&chat_id=${chatId}`);
  }, [chatId, config.token]);

  useEffect(() => {
    getIpLookup({})
      .then(data => setIpAddress(data?.ip))
      .catch(noop);
  }, []);

  const updateEventHandler = useCallback(
    (event: { [key: string]: any }) => {
      if (event.is_via_live_agent) {
        setLiveAgentHandoffInfo(prev => {
          return {
            ...prev,
            is_active: true,
            is_agent_connected: true,
          };
        });
      }

      setChatData(prevChatData => {
        const chatArray = [...prevChatData];
        chatArray.push({
          message: event.data,
          sent: false,
          showLikeDislikeBtn: false,
          id: event.message_id,
          additionalInfo: {
            chatMessageType: event.chat_message_type,
            agentInfo: event.agent_info,
          },
        });
        return chatArray;
      });
      setGenerationMode(null);
    },
    [setChatData, setGenerationMode, setLiveAgentHandoffInfo],
  );

  const ticketSolvedEventHandler = useCallback(
    (event: { [key: string]: any }) => {
      setUseWebsocketGeneration(false);
      setChatData(prevChatData => {
        const chatArray = [...prevChatData];
        chatArray.push({
          message: event.data,
          sent: false,
          showLikeDislikeBtn: false,
          id: event.message_id,
          additionalInfo: {
            chatMessageType: event.chat_message_type,
            agentInfo: event.agent_info,
          },
        });
        return chatArray;
      });
      setGenerationMode(null);
    },
    [setChatData, setGenerationMode, setUseWebsocketGeneration],
  );

  const endEventHandler = useCallback(() => {
    setChatData(prevChatData => {
      const chatArray = [...prevChatData];
      const lastMessage = chatArray.pop();

      chatArray.push({
        ...lastMessage,
        message: removePeriodsAfterURL(lastMessage?.message || ""),
        sent: false,
        showLikeDislikeBtn: false,
      });
      return chatArray;
    });

    setGenerationMode(null);
  }, [setChatData, setGenerationMode]);

  const errorEventHandler = useCallback(() => {
    setChatData(prevChatData => {
      const chatArray = [...prevChatData];
      const message = `Something went wrong. Please get in touch with support${
        supportEmail ? " at " + supportEmail : ""
      }.`;

      chatArray.push({
        message,
        sent: false,
        showLikeDislikeBtn: false,
      });
      return chatArray;
    });

    setGenerationMode(null);
  }, [setChatData, setGenerationMode, supportEmail]);

  const onMessageHandler = useCallback(
    (e: MessageEvent) => {
      if (!useWebsocketGeneration || !e.data) {
        return;
      }
      const event = JSON.parse(e.data);
      if (!!!event) {
        return;
      }
      switch (event.event) {
        case "update": {
          updateEventHandler(event);
          break;
        }
        case "ticket_solved": {
          ticketSolvedEventHandler(event);
          break;
        }
        case "error": {
          errorEventHandler();
          break;
        }
        case "end": {
          endEventHandler();
          break;
        }
      }
    },
    [
      endEventHandler,
      errorEventHandler,
      ticketSolvedEventHandler,
      updateEventHandler,
      useWebsocketGeneration,
    ],
  );

  useEffect(() => {
    if (lastMessage !== null) {
      onMessageHandler(lastMessage);
    }
  }, [lastMessage, onMessageHandler]);

  const handleSendMessage = useCallback(
    (message: string) => sendMessage(message),
    [sendMessage],
  );

  const sendWebsocketMessage = useCallback(
    (
      msg: string,
      chatUserId?: string,
      additionalParams?: { [key: string]: any },
    ): Promise<void> => {
      return new Promise(async (resolve, reject) => {
        if (!botId) {
          reject(new Error("Bot ID is required"));
          return;
        }
        setGenerationMode(generationModes.submitting);

        const parentUrlHref = await new Promise(_resolve =>
          getParentUrl(parentUrl =>
            _resolve(parentUrl ? parentUrl.href : window.location.href),
          ),
        );

        try {
          const params = {
            action: "sendMessage",
            data: {
              input_text: msg,
              token: config.token,
              chat_id: chatId,
              ip_address: ipAddress,
              chat_user_id: chatUserId,
              BaseOrigin: config.baseOrigin,
              page: parentUrlHref,
              ...additionalParams,
            },
          };

          handleSendMessage(JSON.stringify(params));
          if (msg) {
            setChatData(prevChatData => {
              const chatArray = [...prevChatData];
              chatArray.push({
                message: msg,
                sent: true,
              });
              return chatArray;
            });
          }
          setGenerationMode(null);
          setMessage("");
          resolve();
        } catch (err) {
          reject(err);
        }
      });
    },
    [
      botId,
      chatId,
      config.baseOrigin,
      config.token,
      getParentUrl,
      handleSendMessage,
      ipAddress,
      setChatData,
      setGenerationMode,
      setMessage,
    ],
  );

  return {
    sendWebsocketMessage,
  };
}

export default useConnectWebsocket;
